import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Search, ClipboardList, Plus, Eye, ArrowRight, ExternalLink, X, CheckCircle2, PauseCircle, RotateCcw, Pencil, Save, Ban, Download, FileDown } from "lucide-react";
import { StatusBadge, PageHeader, EmptyState, formatDate } from "@/lib/ui";
import { downloadCsv, iso } from "@/lib/csvExport";
import { fetchAndSave } from "@/lib/downloads";

const STATUS_TABS = [
  ["all", "All"],
  ["not_started", "Not Started"],
  ["in_progress", "In Progress"],
  ["waiting_for_information", "Waiting for Information"],
  ["complete", "Complete"],
];

export default function Intake() {
  const { currentOrgId, currentRole } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailIntake, setDetailIntake] = useState(null);   // ← drawer state
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState({ notes: "", status: "" });

  const isAdmin = currentRole === "org_admin";

  useEffect(() => {
    if (params.get("new") === "1") navigate("/app/intake/new", { replace: true });
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    const { data, error } = await supabase.from("intake_records")
      .select("*,clients(id,first_name,last_name,email,phone)")
      .eq("organization_id", currentOrgId).order("updated_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [currentOrgId]);

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (!q) return true;
    const n = `${r.clients?.first_name || ""} ${r.clients?.last_name || ""}`.toLowerCase();
    return n.includes(q.toLowerCase());
  });

  // Status transitions — intake_records is an audit trail. Status updates are
  // safe (existing model); hard delete is intentionally not offered here.
  const _updateStatus = async (record, newStatus, extra = {}) => {
    const patch = { status: newStatus, ...extra };
    if (newStatus === "complete") patch.completed_at = new Date().toISOString();
    if (newStatus !== "complete" && record.status === "complete") patch.completed_at = null;
    const { data, error } = await supabase.from("intake_records")
      .update(patch).eq("id", record.id).select().single();
    if (error) return toast.error(error.message);
    if (!data) return toast.error("Update was blocked. You may not have permission.");
    toast.success(`Status changed to ${newStatus.replace(/_/g, " ")}`);
    setDetailIntake({ ...data, clients: record.clients });
    load();
  };

  const continueIntake = (r) => navigate(`/app/intake/new?resume=${r.id}`);
  const openClientIntake = (r) => navigate(`/app/clients/${r.client_id}?tab=intake`);

  // Enter edit mode with current values pre-filled.
  const startEdit = (r) => { setEditDraft({ notes: r.notes || "", status: r.status }); setEditMode(true); };
  // Save the in-drawer edits (notes + status). Uses the same RLS-safe .select().single() pattern as other pages.
  const saveEdit = async (r) => {
    const patch = { notes: editDraft.notes || null, status: editDraft.status };
    if (editDraft.status === "complete" && r.status !== "complete") patch.completed_at = new Date().toISOString();
    if (editDraft.status !== "complete" && r.status === "complete") patch.completed_at = null;
    const { data, error } = await supabase.from("intake_records").update(patch).eq("id", r.id).select().single();
    if (error) return toast.error(error.message);
    if (!data) return toast.error("Update was blocked. You may not have permission.");
    toast.success("Intake saved");
    setDetailIntake({ ...data, clients: r.clients });
    setEditMode(false);
    load();
  };
  // Intake records are audit history — no hard delete endpoint exists. The
  // safest archive path is a status change to "cancelled" which is a real
  // schema value that preserves the record and its audit trail.
  const cancelIntake = async (r) => {
    if (!window.confirm(`Cancel this intake for ${r.clients?.first_name} ${r.clients?.last_name}?\n\nThe record will be marked "cancelled" but preserved for audit history. Cannot be undone from this page.`)) return;
    await _updateStatus(r, "cancelled");
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="intake-page">
      <PageHeader
        title="Intake"
        subtitle="Guide every client through the intake workflow, step by step."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid="intake-export-csv"
              disabled={filtered.length === 0}
              onClick={() => downloadCsv(
                `intake-${iso(new Date())}`,
                [
                  { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
                  { label: "Client email", get: (r) => r.clients?.email || "" },
                  { label: "Client phone", get: (r) => r.clients?.phone || "" },
                  { key: "status", label: "Status" },
                  { label: "Notes", get: (r) => r.notes || "" },
                  { label: "Created", get: (r) => iso(r.created_at) },
                  { label: "Last updated", get: (r) => iso(r.updated_at) },
                ],
                filtered,
              )}
            ><Download size={14} className="mr-1.5" /> Export CSV</Button>
            <Button data-testid="new-intake-btn" onClick={() => navigate("/app/intake/new")}
              className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              <Plus size={14} className="mr-1.5" /> New intake
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input data-testid="intake-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by client name…" className="pl-9 bg-white border-[#E8E2D9]" />
        </div>
        <div className="flex items-center gap-1 bg-white rounded-full border border-[#E8E2D9] p-1 flex-wrap">
          {STATUS_TABS.map(([k, l]) => (
            <button key={k} data-testid={`ifilter-${k}`} onClick={() => setStatusFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === k ? "bg-[#E8712C] text-white" : "text-[#57534E] hover:text-[#1C1917]"
              }`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No intake records yet"
          description="Start a new intake — the 4-step wizard will create the client record, tasks, and timeline automatically."
          action={
            <Button data-testid="empty-new-intake-btn" onClick={() => navigate("/app/intake/new")}
              className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              <Plus size={14} className="mr-1.5" /> New intake
            </Button>
          } />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#57534E] bg-white rounded-2xl border border-[#E8E2D9]">No intakes match your search.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden divide-y divide-[#E8E2D9]">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`intake-row-${r.id}`}
              onClick={() => setDetailIntake(r)}
              className="flex items-center gap-4 px-6 py-4 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors cursor-pointer">
              <div className="w-10 h-10 rounded-lg bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] text-sm font-bold">
                {(r.clients?.first_name?.[0] || "?").toUpperCase()}{(r.clients?.last_name?.[0] || "").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.clients?.first_name} {r.clients?.last_name}</div>
                <div className="text-xs text-[#57534E]">
                  {r.started_at ? `Started ${formatDate(r.started_at)}` : "Not started"}
                  {r.completed_at && ` · Completed ${formatDate(r.completed_at)}`}
                  {r.current_step && !r.completed_at && ` · Step ${r.current_step}`}
                </div>
              </div>
              <StatusBadge value={r.status} />
              <button data-testid={`view-intake-${r.id}`} onClick={(e) => { e.stopPropagation(); setDetailIntake(r); }}
                className="text-xs px-2 py-1 rounded-full text-[#57534E] hover:bg-[#FAF8F5] flex items-center gap-1">
                <Eye size={12} /> View
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ─── Intake Detail Drawer ────────────────────────────────────────── */}
      <Sheet open={!!detailIntake} onOpenChange={(v) => { if (!v) { setDetailIntake(null); setEditMode(false); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="intake-drawer">
          {detailIntake && (() => {
            const r = detailIntake;
            const c = r.clients || {};
            const isComplete = r.status === "complete";
            return (
              <>
                <div className="pr-8 pb-3 border-b border-[#E8E2D9]">
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Client Intake</div>
                  <div className="font-display text-2xl tracking-tight" data-testid="intake-drawer-title">{c.first_name} {c.last_name}</div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <StatusBadge value={r.status} testid="intake-drawer-status" />
                    {r.current_step && !isComplete && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDE9D8] text-[#B85C1F]">
                        Step {r.current_step}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Email</div><div className="mt-0.5">{c.email || <span className="text-[#A8A29E]">—</span>}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Phone</div><div className="mt-0.5">{c.phone || <span className="text-[#A8A29E]">—</span>}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Started</div><div className="mt-0.5" data-testid="intake-drawer-started">{r.started_at ? formatDate(r.started_at) : <span className="text-[#A8A29E]">Not started</span>}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Completed</div><div className="mt-0.5" data-testid="intake-drawer-completed">{r.completed_at ? formatDate(r.completed_at) : <span className="text-[#A8A29E]">—</span>}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Created</div><div className="mt-0.5">{formatDate(r.created_at)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Last updated</div><div className="mt-0.5">{formatDate(r.updated_at)}</div></div>
                  </div>

                  {editMode ? (
                    <div className="pt-3 border-t border-[#E8E2D9] space-y-3" data-testid="intake-edit-form">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Status</div>
                        <select data-testid="intake-edit-status" value={editDraft.status} onChange={(e) => setEditDraft({...editDraft, status: e.target.value})}
                          className="w-full border border-[#E8E2D9] rounded-lg px-3 py-2 text-sm bg-white">
                          {["not_started","in_progress","waiting_for_information","complete","cancelled"].map((s) => (
                            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Notes</div>
                        <textarea data-testid="intake-edit-notes" rows={4} value={editDraft.notes} onChange={(e) => setEditDraft({...editDraft, notes: e.target.value})}
                          className="w-full border border-[#E8E2D9] rounded-lg px-3 py-2 text-sm bg-white" />
                      </div>
                    </div>
                  ) : r.notes && (
                    <div className="pt-3 border-t border-[#E8E2D9]">
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Notes</div>
                      <div className="text-sm whitespace-pre-wrap" data-testid="intake-drawer-notes">{r.notes}</div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-[#E8E2D9]">
                    <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-2">Actions</div>
                    <div className="flex flex-wrap gap-2">
                      {!editMode && !isComplete && (
                        <Button data-testid="intake-continue-btn" onClick={() => continueIntake(r)}
                          className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                          <ArrowRight size={13} className="mr-1.5" /> Continue intake
                        </Button>
                      )}
                      {!editMode && isAdmin && (
                        <Button data-testid="intake-edit-btn" onClick={() => startEdit(r)}
                          variant="outline" className="rounded-full">
                          <Pencil size={13} className="mr-1.5" /> Edit
                        </Button>
                      )}
                      {editMode && (
                        <>
                          <Button data-testid="intake-save-btn" onClick={() => saveEdit(r)}
                            className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                            <Save size={13} className="mr-1.5" /> Save
                          </Button>
                          <Button data-testid="intake-cancel-edit-btn" onClick={() => setEditMode(false)}
                            variant="outline" className="rounded-full">Cancel edit</Button>
                        </>
                      )}
                      {!editMode && isAdmin && !isComplete && r.status !== "waiting_for_information" && (
                        <Button data-testid="intake-request-info-btn" onClick={() => _updateStatus(r, "waiting_for_information")}
                          variant="outline" className="rounded-full text-amber-700 border-amber-200">
                          <PauseCircle size={13} className="mr-1.5" /> Request information
                        </Button>
                      )}
                      {!editMode && isAdmin && !isComplete && (
                        <Button data-testid="intake-complete-btn" onClick={() => _updateStatus(r, "complete")}
                          variant="outline" className="rounded-full text-emerald-700 border-emerald-200">
                          <CheckCircle2 size={13} className="mr-1.5" /> Mark complete
                        </Button>
                      )}
                      {!editMode && isAdmin && isComplete && (
                        <Button data-testid="intake-reopen-btn" onClick={() => _updateStatus(r, "in_progress")}
                          variant="outline" className="rounded-full">
                          <RotateCcw size={13} className="mr-1.5" /> Reopen
                        </Button>
                      )}
                      {!editMode && isAdmin && r.status !== "cancelled" && (
                        <Button data-testid="intake-cancel-btn" onClick={() => cancelIntake(r)}
                          variant="outline" className="rounded-full text-red-600 border-red-200">
                          <Ban size={13} className="mr-1.5" /> Cancel intake
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#E8E2D9] flex items-center gap-2 flex-wrap">
                    <Button data-testid="intake-open-profile-btn" onClick={() => openClientIntake(r)}
                      variant="outline" className="rounded-full">
                      <ExternalLink size={13} className="mr-1.5" /> Open client profile
                    </Button>
                    <Button
                      data-testid="intake-drawer-pdf-btn"
                      onClick={async () => {
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session) return toast.error("Please sign in again");
                          const slug = `intake-${c?.first_name || "client"}-${c?.last_name || ""}`.replace(/\s+/g, "-").toLowerCase();
                          await fetchAndSave(
                            `${process.env.REACT_APP_BACKEND_URL}/api/intake-records/${r.id}/pdf`,
                            { token: session.access_token, filename: `${slug}.pdf` }
                          );
                        } catch (e) { toast.error(e.message || "PDF download failed"); }
                      }}
                      variant="outline" className="rounded-full"
                    ><FileDown size={13} className="mr-1.5" /> Download PDF</Button>
                    <Button data-testid="intake-drawer-close" onClick={() => setDetailIntake(null)}
                      variant="ghost" className="rounded-full ml-auto">
                      <X size={13} className="mr-1" /> Close
                    </Button>
                  </div>

                  <div className="pt-3 border-t border-[#E8E2D9]">
                    <div className="text-[10px] text-[#A8A29E] italic">
                      Intake records are part of the client audit trail. To edit answers or upload
                      documents attached to this intake, use "Open client profile" — the intake tab
                      there is the full editor.
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
